import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AiSection,
  BpmSection,
  CostsSection,
  GeneralSection,
  RiskSection,
  SourcesSection,
  TradingSection,
  UserSection,
  ValuationSection,
} from "@/components/settings/SettingsSections";

const TABS = [
  { key: "general", label: "Algemeen" },
  { key: "trading", label: "Handel" },
  { key: "costs", label: "Kostenaannames" },
  { key: "valuation", label: "Waardering" },
  { key: "bpm", label: "BPM-regels" },
  { key: "risk", label: "Risico's" },
  { key: "sources", label: "Databronnen" },
  { key: "ai", label: "AI" },
  { key: "user", label: "Account" },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("general");
  const [settings, setSettings] = useState(null);
  const [labels, setLabels] = useState({});
  const [tariffs, setTariffs] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [depreciation, setDepreciation] = useState([]);
  const [providers, setProviders] = useState([]);
  const [riskCatalog, setRiskCatalog] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const tariffYears = useMemo(
    () => [...new Set(periods.map((p) => p.year))].sort((a, b) => b - a),
    [periods],
  );

  const load = async () => {
    try {
      const [s, t, r, risks] = await Promise.all([
        api.get("/settings"),
        api.get("/bpm/tariffs"),
        api.get("/reference"),
        api.get("/risks"),
      ]);
      setSettings(s.data.settings);
      setLabels(s.data.cost_labels);
      setTariffs(t.data.tariffs);
      setPeriods(t.data.periods || []);
      setIntegrity(t.data.integrity || null);
      setDepreciation(t.data.depreciation_table);
      setProviders(r.data.sources);
      setRiskCatalog(risks.data.catalog || []);
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => {
    // Eenmalig bij openen: alle instellingen, tarieven en bronnen ophalen.
    load();
  }, []);

  const setSection = (section, key, value) =>
    setSettings((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));

  const save = async (sections) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const body = {};
      sections.forEach((s) => {
        body[s] = settings[s];
      });
      const { data } = await api.put("/settings", body);
      setSettings(data);
      setMessage("Instellingen opgeslagen. Nieuwe berekeningen gebruiken deze waarden.");
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const reloadTariffs = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const { data } = await api.post("/bpm/tariffs/reload");
      setPeriods(data.periods || []);
      setIntegrity(data.integrity || null);
      setMessage(
        data.integrity?.passed
          ? "Officiële BPM-tarieven 1993-2026 geladen en gecontroleerd."
          : "Tarieven geladen, maar de integriteitscheck vond fouten.",
      );
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  const changePassword = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/change-password", pw);
      setMessage(data.message);
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      setError(apiError(e));
    }
    setBusy(false);
  };

  if (!settings) {
    return (
      <AppShell title="Instellingen">
        <div className="panel px-4 py-10 text-center text-[13px] text-slate-500">Laden…</div>
      </AppShell>
    );
  }

  const shared = { settings, setSection, onSave: save, busy };

  return (
    <AppShell title="Instellingen" subtitle="Alle aannames zijn configureerbaar en worden gelabeld">
      <div className="mb-3 flex flex-wrap gap-1" data-testid="settings-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`settings-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-sm border px-2.5 py-1.5 text-[12px] font-semibold transition-colors duration-150 ${
              tab === t.key
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {message && (
        <div className="mb-3 rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800" data-testid="settings-success">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      )}

      {tab === "general" && <GeneralSection {...shared} />}
      {tab === "trading" && <TradingSection {...shared} />}
      {tab === "costs" && <CostsSection {...shared} labels={labels} />}
      {tab === "valuation" && <ValuationSection {...shared} />}
      {tab === "bpm" && (
        <BpmSection
          settings={settings}
          setSection={setSection}
          tariffYears={tariffYears}
          periods={periods}
          integrity={integrity}
          depreciation={depreciation}
          busy={busy}
          onSave={save}
          reloadTariffs={reloadTariffs}
        />
      )}
      {tab === "risk" && (
        <RiskSection riskCatalog={riskCatalog} settings={settings} setSettings={setSettings} onSave={save} busy={busy} />
      )}
      {tab === "sources" && <SourcesSection providers={providers} />}
      {tab === "ai" && <AiSection {...shared} />}
      {tab === "user" && (
        <UserSection user={user} pw={pw} setPw={setPw} changePassword={changePassword} busy={busy} />
      )}
    </AppShell>
  );
}
